import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from './AuthContext';
import {
  FriendsService,
  Friendship,
  GameInvite,
  SessionMessage,
} from '../../services/firebase/friendsService';
import { registerPresence, subscribeToFriendPresence } from '../../services/firebase/presenceService';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Friend {
  uid: string;
  username: string;
  friendTag: string;
  zodiacResult?: string;
  isOnline: boolean;
  friendshipId: string;
}

export interface FriendRequest {
  friendshipId: string;
  fromUid: string;
  fromUsername: string;
  fromFriendTag: string;
  fromZodiac?: string;
  requestedAt: Date;
}

export interface MessageNotification {
  friendshipId: string;
  friend: Friend;
  preview: string;
}

interface FriendsContextValue {
  // Friend data
  friends: Friend[];
  pendingReceived: FriendRequest[];
  pendingSent: FriendRequest[];
  incomingInvites: GameInvite[];
  unreadCount: number;
  unreadMessageSenders: number;
  unreadByConversationId: Set<string>;
  anyFriendOnline: boolean;

  // Notifications
  onlineNotifications: Friend[];
  messageNotifications: MessageNotification[];
  dismissOnlineNotification: (uid: string) => void;
  dismissMessageNotification: (friendshipId: string) => void;

  // Actions
  sendRequest: (usernameWithTag: string) => Promise<{ success: boolean; error?: string }>;
  acceptRequest: (friendshipId: string) => void;
  declineRequest: (friendshipId: string) => void;
  removeFriend: (friendshipId: string) => void;
  sendGameInvite: (friendUid: string, gameId: string, timeControl: string) => Promise<{ success: boolean; error?: string }>;
  acceptGameInvite: (inviteId: string) => Promise<string>;
  declineGameInvite: (inviteId: string) => void;

  // Messaging
  activeConversation: string | null;
  activeConversationFriend: Friend | null;
  messages: SessionMessage[];
  openConversation: (friendUid: string) => void;
  closeConversation: () => void;
  sendMessage: (text: string) => void;

  // Sidebar
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
}

// ─── Context ────────────────────────────────────────────────────────────────

export const FriendsContext = createContext<FriendsContextValue>({
  friends: [],
  pendingReceived: [],
  pendingSent: [],
  incomingInvites: [],
  unreadCount: 0,
  unreadMessageSenders: 0,
  unreadByConversationId: new Set(),
  anyFriendOnline: false,
  onlineNotifications: [],
  messageNotifications: [],
  dismissOnlineNotification: () => {},
  dismissMessageNotification: () => {},
  sendRequest: async () => ({ success: false }),
  acceptRequest: () => {},
  declineRequest: () => {},
  removeFriend: () => {},
  sendGameInvite: async () => ({ success: false }),
  acceptGameInvite: async () => '',
  declineGameInvite: () => {},
  activeConversation: null,
  activeConversationFriend: null,
  messages: [],
  openConversation: () => {},
  closeConversation: () => {},
  sendMessage: () => {},
  isSidebarOpen: false,
  openSidebar: () => {},
  closeSidebar: () => {},
  toggleSidebar: () => {},
});

// ─── Read Timestamp Persistence ─────────────────────────────────────────────

const READ_TIMESTAMPS_KEY_PREFIX = 'zodiac_read_timestamps_';

function loadReadTimestamps(uid: string): Map<string, number> {
  try {
    const stored = localStorage.getItem(READ_TIMESTAMPS_KEY_PREFIX + uid);
    if (stored) {
      const obj = JSON.parse(stored);
      return new Map(Object.entries(obj));
    }
  } catch { }
  return new Map();
}

function saveReadTimestamps(uid: string, map: Map<string, number>): void {
  try {
    const obj = Object.fromEntries(map);
    localStorage.setItem(READ_TIMESTAMPS_KEY_PREFIX + uid, JSON.stringify(obj));
  } catch { }
}

// ─── Provider ───────────────────────────────────────────────────────────────

export function FriendsProvider({ children }: { children: React.ReactNode }) {
  const { user, userProfile } = useContext(AuthContext);
  const uid = user?.uid;

  // State
  const [friendships, setFriendships] = useState<Friendship[]>([]);
  const [onlineUids, setOnlineUids] = useState<Set<string>>(new Set());
  const [incomingInvites, setIncomingInvites] = useState<GameInvite[]>([]);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<SessionMessage[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [readTimestamps, setReadTimestamps] = useState<Map<string, number>>(() =>
    uid ? loadReadTimestamps(uid) : new Map()
  );
  const [onlineNotifications, setOnlineNotifications] = useState<Friend[]>([]);
  const [messageNotifications, setMessageNotifications] = useState<MessageNotification[]>([]);

  const messageUnsubRef = useRef<(() => void) | null>(null);
  const prevOnlineUidsRef = useRef<Set<string> | null>(null);
  const prevMessageTimesRef = useRef<Map<string, number>>(new Map());
  const onlineNotifCooldownRef = useRef<Map<string, number>>(new Map());

  // ─── Derived State ──────────────────────────────────────────────────────

  const friends: Friend[] = friendships
    .filter((f) => f.status === 'accepted')
    .map((f) => {
      const friendUid = f.users.find((u) => u !== uid)!;
      const profile = f.userProfiles?.[friendUid];
      return {
        uid: friendUid,
        username: profile?.username || 'Unknown',
        friendTag: profile?.friendTag || '000',
        zodiacResult: profile?.zodiacResult,
        isOnline: onlineUids.has(friendUid),
        friendshipId: f.id,
      };
    })
    .sort((a, b) => {
      // Online first, then alphabetical
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return a.username.localeCompare(b.username);
    });

  const pendingReceived: FriendRequest[] = friendships
    .filter((f) => f.status === 'pending' && f.requestedBy !== uid)
    .map((f) => {
      const fromUid = f.requestedBy;
      const profile = f.userProfiles?.[fromUid];
      return {
        friendshipId: f.id,
        fromUid,
        fromUsername: profile?.username || 'Unknown',
        fromFriendTag: profile?.friendTag || '000',
        fromZodiac: profile?.zodiacResult,
        requestedAt: f.requestedAt,
      };
    });

  const pendingSent: FriendRequest[] = friendships
    .filter((f) => f.status === 'pending' && f.requestedBy === uid)
    .map((f) => {
      const toUid = f.users.find((u) => u !== uid)!;
      const profile = f.userProfiles?.[toUid];
      return {
        friendshipId: f.id,
        fromUid: toUid,
        fromUsername: profile?.username || 'Unknown',
        fromFriendTag: profile?.friendTag || '000',
        fromZodiac: profile?.zodiacResult,
        requestedAt: f.requestedAt,
      };
    });

  // Collect friendship IDs with unread messages from friends
  const unreadByConversationId = new Set<string>();
  for (const f of friendships) {
    if (f.status !== 'accepted' || !f.lastMessageBy || !f.lastMessageAt) continue;
    if (f.lastMessageBy === uid) continue; // We sent the last message
    // If conversation is currently open, not unread
    if (activeConversation === f.id) continue;
    const readAt = readTimestamps.get(f.id) || 0;
    if (f.lastMessageAt.getTime() > readAt) {
      unreadByConversationId.add(f.id);
    }
  }
  const unreadMessages = unreadByConversationId.size;

  const unreadCount = pendingReceived.length + incomingInvites.length + unreadMessages;
  const unreadMessageSenders = unreadMessages;
  const anyFriendOnline = friends.some((f) => f.isOnline);

  const activeConversationFriend = activeConversation
    ? friends.find((f) => FriendsService.getConversationId(uid!, f.uid) === activeConversation) || null
    : null;

  // ─── Real-time Listeners ────────────────────────────────────────────────

  // Register presence so friends can see us online
  useEffect(() => {
    if (!uid) return;
    registerPresence(uid);
  }, [uid]);

  // Reload read timestamps when uid becomes available (login / account switch)
  useEffect(() => {
    if (uid) {
      setReadTimestamps(loadReadTimestamps(uid));
    }
  }, [uid]);

  // Persist read timestamps to localStorage whenever they change
  useEffect(() => {
    if (uid && readTimestamps.size > 0) {
      saveReadTimestamps(uid, readTimestamps);
    }
  }, [uid, readTimestamps]);

  // Subscribe to friendships
  useEffect(() => {
    if (!uid) return;
    return FriendsService.subscribeFriendships(uid, setFriendships);
  }, [uid]);

  // Subscribe to friend online status (with transition detection)
  useEffect(() => {
    const friendUids = friends.map((f) => f.uid);
    if (friendUids.length === 0) {
      setOnlineUids(new Set());
      return;
    }
    return subscribeToFriendPresence(friendUids, (newUids) => {
      // Detect who just came online
      const prev = prevOnlineUidsRef.current;
      if (prev) {
        const now = Date.now();
        const cooldown = onlineNotifCooldownRef.current;
        const COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes
        const nowOnline: Friend[] = [];
        newUids.forEach((u) => {
          if (!prev.has(u)) {
            const lastNotified = cooldown.get(u) || 0;
            if (now - lastNotified >= COOLDOWN_MS) {
              const f = friends.find((fr) => fr.uid === u);
              if (f) {
                nowOnline.push(f);
                cooldown.set(u, now);
              }
            }
          }
        });
        if (nowOnline.length > 0) {
          setOnlineNotifications((cur) => [
            ...cur,
            ...nowOnline.filter((n) => !cur.some((c) => c.uid === n.uid)),
          ]);
        }
      }
      prevOnlineUidsRef.current = new Set(newUids);
      setOnlineUids(newUids);
    });
  }, [friendships]); // re-subscribe when friendships change

  // Detect new incoming messages for toast notifications
  useEffect(() => {
    if (!uid) return;
    const prev = prevMessageTimesRef.current;
    const next = new Map<string, number>();
    const newNotifs: MessageNotification[] = [];

    for (const f of friendships) {
      if (f.status !== 'accepted' || !f.lastMessageBy || !f.lastMessageAt) continue;
      const ts = f.lastMessageAt.getTime();
      next.set(f.id, ts);
      // Only notify if: from a friend, timestamp changed, sidebar closed, conversation not active
      if (
        f.lastMessageBy !== uid &&
        prev.has(f.id) &&
        ts > (prev.get(f.id) || 0) &&
        !isSidebarOpen &&
        activeConversation !== f.id
      ) {
        const friendUid = f.users.find((u) => u !== uid)!;
        const profile = f.userProfiles?.[friendUid];
        newNotifs.push({
          friendshipId: f.id,
          friend: {
            uid: friendUid,
            username: profile?.username || 'Unknown',
            friendTag: profile?.friendTag || '000',
            zodiacResult: profile?.zodiacResult,
            isOnline: onlineUids.has(friendUid),
            friendshipId: f.id,
          },
          preview: 'New message',
        });
      }
    }
    prevMessageTimesRef.current = next;

    if (newNotifs.length > 0) {
      setMessageNotifications((cur) => [
        ...cur,
        ...newNotifs.filter((n) => !cur.some((c) => c.friendshipId === n.friendshipId)),
      ]);
    }
  }, [friendships, uid, isSidebarOpen, activeConversation]);

  // Subscribe to game invites
  useEffect(() => {
    if (!uid) return;
    return FriendsService.subscribeGameInvites(uid, setIncomingInvites);
  }, [uid]);

  // Subscribe to active conversation messages
  useEffect(() => {
    if (messageUnsubRef.current) {
      messageUnsubRef.current();
      messageUnsubRef.current = null;
    }

    if (!activeConversation) {
      setMessages([]);
      return;
    }

    const unsub = FriendsService.subscribeMessages(activeConversation, setMessages);
    messageUnsubRef.current = unsub;

    return () => {
      unsub();
      messageUnsubRef.current = null;
    };
  }, [activeConversation]);

  // ─── Actions ────────────────────────────────────────────────────────────

  const sendRequest = useCallback(async (usernameWithTag: string) => {
    if (!uid || !userProfile) return { success: false, error: 'Not logged in' };

    const found = await FriendsService.searchUser(usernameWithTag);
    if (!found) return { success: false, error: 'User not found' };

    return FriendsService.sendFriendRequest(
      uid,
      found.uid,
      {
        username: userProfile.username,
        friendTag: userProfile.friendTag || '000',
        zodiacResult: userProfile.zodiacResult,
      },
      {
        username: found.username,
        friendTag: found.friendTag,
        zodiacResult: found.zodiacResult,
      }
    );
  }, [uid, userProfile]);

  const acceptRequest = useCallback((friendshipId: string) => {
    FriendsService.acceptFriendRequest(friendshipId);
  }, []);

  const declineRequest = useCallback((friendshipId: string) => {
    FriendsService.removeFriendship(friendshipId);
  }, []);

  const removeFriend = useCallback((friendshipId: string) => {
    FriendsService.removeFriendship(friendshipId);
  }, []);

  const sendGameInvite = useCallback(async (friendUid: string, gameId: string, timeControl: string) => {
    if (!uid || !userProfile) return { success: false, error: 'Not logged in' };

    return FriendsService.sendGameInvite(
      uid,
      userProfile.username,
      userProfile.friendTag || '000',
      friendUid,
      gameId,
      timeControl
    );
  }, [uid, userProfile]);

  const acceptGameInvite = useCallback(async (inviteId: string) => {
    return FriendsService.acceptGameInvite(inviteId);
  }, []);

  const declineGameInvite = useCallback((inviteId: string) => {
    FriendsService.declineGameInvite(inviteId);
  }, []);

  const openConversation = useCallback((friendUid: string) => {
    if (!uid) return;
    const convId = FriendsService.getConversationId(uid, friendUid);
    setActiveConversation(convId);
    // Mark as read
    setReadTimestamps((prev) => {
      const next = new Map(prev);
      next.set(convId, Date.now());
      return next;
    });
  }, [uid]);

  const closeConversation = useCallback(() => {
    setActiveConversation(null);
    setMessages([]);
  }, []);

  const sendMessage = useCallback((text: string) => {
    if (!uid || !activeConversation) return;
    FriendsService.sendMessage(activeConversation, uid, text);
  }, [uid, activeConversation]);

  const dismissOnlineNotification = useCallback((friendUid: string) => {
    setOnlineNotifications((cur) => cur.filter((f) => f.uid !== friendUid));
  }, []);

  const dismissMessageNotification = useCallback((friendshipId: string) => {
    setMessageNotifications((cur) => cur.filter((n) => n.friendshipId !== friendshipId));
  }, []);

  const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
    closeConversation();
  }, [closeConversation]);
  const toggleSidebar = useCallback(() => setIsSidebarOpen((prev) => !prev), []);

  // ─── Context Value ──────────────────────────────────────────────────────

  const value: FriendsContextValue = {
    friends,
    pendingReceived,
    pendingSent,
    incomingInvites,
    unreadCount,
    unreadMessageSenders,
    unreadByConversationId,
    anyFriendOnline,
    onlineNotifications,
    messageNotifications,
    dismissOnlineNotification,
    dismissMessageNotification,
    sendRequest,
    acceptRequest,
    declineRequest,
    removeFriend,
    sendGameInvite,
    acceptGameInvite,
    declineGameInvite,
    activeConversation,
    activeConversationFriend,
    messages,
    openConversation,
    closeConversation,
    sendMessage,
    isSidebarOpen,
    openSidebar,
    closeSidebar,
    toggleSidebar,
  };

  return (
    <FriendsContext.Provider value={value}>
      {children}
    </FriendsContext.Provider>
  );
}
